
import { FileFormat } from 'app/model/dataset/file-format';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { TextFormat } from 'app/model/dataset/text-format';
import { IModel } from 'app/model/base/annotation';
import { JSONFormat } from 'app/model/dataset/json.format';
import { fileFormatConstants, ModelConstants } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

export const fileFormats = {
    avroFormat: 'AvroFormat',
    jsonFormat: 'JsonFormat',
    orcFormat: 'OrcFormat',
    parquetFormat: 'ParquetFormat',
    textFormat: 'TextFormat',
    delimitedTextFormat: 'DelimitedTextFormat',
    noFormat: 'NoFormat'
};

export const columnDelimiters = {
    comma: ',',
    pipe: '|',
    semicolon: ';',
    startOfHeading: '\u0001',
    tab: '\t'
};

export const rowDelimiters = {
    both: '',
    carriageReturn: '\r',
    lineFeed: '\n',
    carriageReturnAndLineFeed: '\r\n'
};

export const filePatterns = {
    setOfObjects: 'setOfObjects',
    arrayOfObjects: 'arrayOfObjects'
};

export const fileFormatOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.ModelName_DelimitedTextDataset,
        value: fileFormats.textFormat
    },
    {
        label: stringResources.ModelName_AvroDataset,
        value: fileFormats.avroFormat
    },
    {
        label: stringResources.ModelName_JsonDataset,
        value: fileFormats.jsonFormat
    },
    {
        label: stringResources.ModelName_OrcDataset,
        value: fileFormats.orcFormat
    },
    {
        label: stringResources.ModelName_ParquetDataset,
        value: fileFormats.parquetFormat
    }
];

export const fileFormatOptionsForV2: Array<ISelectOption<string>> = [
    {
        label: stringResources.ModelName_DelimitedTextDataset,
        value: ModelConstants.delimitedTextDataset
    },
    {
        label: stringResources.ModelName_AvroDataset,
        value: ModelConstants.avroDataset
    },
    {
        label: stringResources.ModelName_JsonDataset,
        value: ModelConstants.jsonDataset
    },
    {
        label: stringResources.ModelName_OrcDataset,
        value: ModelConstants.orcDataset
    },
    {
        label: stringResources.ModelName_ParquetDataset,
        value: ModelConstants.parquetDataset
    }
];

export const fileFormatOptionsForV2Full: Array<ISelectOption<string>> = [
    ...fileFormatOptionsForV2,
    {
        label: stringResources.ModelName_BinaryDataset,
        value: ModelConstants.binaryDataset
    },
    {
        label: stringResources.ModelName_ExcelDataset,
        value: ModelConstants.excelDataset
    },
    {
        label: stringResources.ModelName_XmlDataset,
        value: ModelConstants.xmlDataset
    },
    {
        label: stringResources.Iceberg_DisplayText,
        value: adfModelTypeMap.iceberg.datasetType
    }
].concat(GlobalVariables.supportDelta ?
    [{
        label: stringResources.ModelName_DelaFormatDataset,
        value: adfModelTypeMap.delta.datasetType
    }] :
    []
).sort((a, b) => a.label.localeCompare(b.label));

export const fileFormatOptionsForCopyJob: Array<ISelectOption<string>> = [
    {
        label: stringResources.ModelName_DelimitedTextDataset,
        value: ModelConstants.delimitedTextDataset
    },
    {
        label: stringResources.ModelName_ParquetDataset,
        value: ModelConstants.parquetDataset
    },
    {
        label: stringResources.ModelName_JsonDataset,
        value: ModelConstants.jsonDataset
    },
    {
        label: stringResources.ModelName_AvroDataset,
        value: ModelConstants.avroDataset
    },
    {
        label: stringResources.ModelName_OrcDataset,
        value: ModelConstants.orcDataset
    },
    {
        label: stringResources.ModelName_ExcelDataset,
        value: ModelConstants.excelDataset
    },
    {
        label: stringResources.ModelName_XmlDataset,
        value: ModelConstants.xmlDataset
    }
].concat(GlobalVariables.supportDelta || GlobalVariables.supportDeltaCloneCopyJob ?
    [{
        label: stringResources.ModelName_DelaFormatDataset,
        value: adfModelTypeMap.delta.datasetType
    }] :
    []
).sort((a, b) => a.label.localeCompare(b.label));

export const dataflowFileFormatOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.ModelName_DelimitedTextDataset,
        value: fileFormats.textFormat
    }, {
        label: stringResources.ModelName_ParquetDataset,
        value: fileFormats.parquetFormat
    }
];

export const columnDelimiterOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterComma,
        value: columnDelimiters.comma
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterSemicolon,
        value: columnDelimiters.semicolon
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterPipe,
        value: columnDelimiters.pipe
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterTab,
        value: columnDelimiters.tab
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterStartOfHeading,
        value: columnDelimiters.startOfHeading
    }
];

export const columnDelimiterWithoutNoneV2Options: Array<ISelectOption<string>> = [
    {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterComma,
        value: columnDelimiters.comma
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterSemicolon,
        value: columnDelimiters.semicolon
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterPipe,
        value: columnDelimiters.pipe
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterTab,
        value: columnDelimiters.tab
    }, {
        label: stringResources.Dataset_FileFormat_ColumnDelimiterStartOfHeading,
        value: columnDelimiters.startOfHeading
    }
];

// Copy/Lookup/Get metadata activity doesn't support NoDelimiter. So we should use columnDelimiterWithoutNoneV2Options for copy scenarios
export const columnDelimiterV2Options: Array<ISelectOption<string>> = [...columnDelimiterWithoutNoneV2Options, {
    label: stringResources.NoDelimiterLabel,
    value: ''
}
];

export const rowDelimiterOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.Dataset_FileFormat_RowDelimiterBoth,
        value: rowDelimiters.both
    }, {
        label: stringResources.Dataset_FileFormat_RowDelimiterLineFeed,
        value: rowDelimiters.lineFeed
    }, {
        label: stringResources.Dataset_FileFormat_RowDelimiterCarriageReturn,
        value: rowDelimiters.carriageReturn
    }
];

export const rowDelimiterWithoutNoneV2Options: Array<ISelectOption<string>> = [
    {
        label: stringResources.Dataset_FileFormat_RowDelimiterAutoDetect,
        value: undefined
    }, {
        label: stringResources.Dataset_FileFormat_RowDelimiterLineFeed,
        value: rowDelimiters.lineFeed
    }, {
        label: stringResources.Dataset_FileFormat_RowDelimiterCarriageReturn,
        value: rowDelimiters.carriageReturn
    }
].concat(__IS_FABRIC_DPE__ ? [
    {
        label: stringResources.Dataset_FileFormat_RowDelimiterBoth,
        value: rowDelimiters.carriageReturnAndLineFeed
    }
] : []);

// Copy/Lookup/Get metadata activity doesn't support NoDelimiter. So we should use rowDelimiterWithoutNoneV2Options for copy scenarios
export const rowDelimiterV2Options: Array<ISelectOption<string>> = [...rowDelimiterWithoutNoneV2Options, {
    label: stringResources.NoDelimiterLabel,
    value: ''
}];

export const charValueConst = {
    SingleChar: '\'',
    EmptyChar: '',
    DoubleQuoteChar: '"'
};

export const quoteCharV2Options: Array<ISelectOption<string>> = [
    {
        label: stringResources.Dataset_FileFormat_QuoteCharDouble,
        value: charValueConst.DoubleQuoteChar
    },
    {
        label: stringResources.Dataset_FileFormat_QuoteCharSingle,
        value: charValueConst.SingleChar
    },
    {
        label: stringResources.Dataset_FileFormat_QuoteCharNone,
        value: charValueConst.EmptyChar
    }
];

export const escapeCharV2Options: Array<ISelectOption<string>> = [
    {
        label: stringResources.Dataset_FileFormat_EscapeCharBacklash,
        value: '\\'
    },
    {
        label: stringResources.Dataset_FileFormat_EscapeCharSlash,
        value: '/'
    },
    {
        label: stringResources.Dataset_FileFormat_QuoteCharDouble,
        value: charValueConst.DoubleQuoteChar
    },
    {
        label: stringResources.Dataset_FileFormat_EscapeCharNone,
        value: ''
    }
];

export const jsonNodeReferenceOptions: Array<ISelectOption<string | undefined>> = [
    {
        label: stringResources.None,
        value: undefined
    }
];

export const filePatternOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.Dataset_FileFormat_SetOfObjects,
        value: filePatterns.setOfObjects
    }, {
        label: stringResources.Dataset_FileFormat_ArrayOfObjects,
        value: filePatterns.arrayOfObjects
    }
];

export const nullValueDefault = '\\N';
export const nullValueDefaultForCsv = '';
export const quoteCharDefaultForCsv: string = fileFormatConstants.quoteCharDefaultForCsv;
export const escapeCharDefaultForCsv: string = fileFormatConstants.escapeCharDefaultForCsv;
export const textFormatValue: TextFormat = new TextFormat();
textFormatValue.columnDelimiter = columnDelimiters.comma;
textFormatValue.rowDelimiter = rowDelimiters.both;
textFormatValue.skipLineCount = 0;
textFormatValue.firstRowAsHeader = false;
textFormatValue.treatEmptyAsNull = true;
textFormatValue.nullValue = nullValueDefault;

export const initialFormatValue: FileFormat = new FileFormat();
initialFormatValue.type = fileFormats.textFormat;
initialFormatValue.typeFormat = textFormatValue;

export const formatModelMap: { [key: string]: IModel } = {
    [fileFormats.jsonFormat]: JSONFormat.MODEL,
    [fileFormats.textFormat]: TextFormat.MODEL
};
